/* eslint-disable @next/next/no-img-element */

import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { Fragment, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { cn } from '@/utils';
import { buildGumletUrl } from '@/utils/gumlet';

type Props = {
  images: string[];
  onSelect?: (index: number) => void;
};

export type CarouselHandle = {
  scrollTo: (index: number) => void;
  selectedIndex: number;
};

const ImageCarousel = forwardRef<CarouselHandle, Props>(({ images, onSelect }, ref) => {
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 4000 }),
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleSelect = useCallback(() => {
    if (!emblaMainApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi]);

  useEffect(() => {
    if (typeof onSelect === 'function') onSelect(selectedIndex);
  }, [selectedIndex, onSelect]);

  useImperativeHandle(
    ref,
    () => ({
      scrollTo: (index: number) => emblaMainApi?.scrollTo(index),
      selectedIndex,
    }),
    [emblaMainApi, selectedIndex],
  );

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaMainApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi],
  );

  useEffect(() => {
    if (!emblaMainApi) return;
    handleSelect();
    emblaMainApi.on('select', handleSelect).on('reInit', handleSelect);
  }, [emblaMainApi, handleSelect]);

  return (
    // Carousel must have an explicit width: the parent flex column gives it
    // a column track but no horizontal sizing. Using `w-fit` (as before) plus
    // child images sized `w-full` created a circular layout where Embla had
    // no width to clip against and the next slide leaked into view. Pin the
    // width to 640px (matches each slide's intrinsic size) and let the column
    // shrink it to 100% on small screens.
    <div className="w-full max-w-[640px]">
      <div ref={emblaMainRef} className="overflow-hidden">
        <div className="flex gap-1">
          {images.map((item, idx) => {
            // The first slide is the LCP candidate. Load it eagerly and with
            // high fetch priority; lazy-load the rest so they don't compete
            // for bandwidth before the hero paints.
            const isLcp = idx === 0;
            // Ask Gumlet for an appropriately sized variant. The hero box is
            // ~640px wide on desktop; serving a 1280w variant covers high-DPR
            // screens without paying for a full original (often 2-4 MB).
            const optimizedSrc = buildGumletUrl(item, { width: 1280, quality: 75 });
            return (
              <Fragment key={idx}>
                <div className="flex min-w-0 shrink-0 grow-0 basis-full justify-center">
                  <img
                    alt="product"
                    src={optimizedSrc}
                    width={640}
                    height={640}
                    loading={isLcp ? 'eager' : 'lazy'}
                    decoding={isLcp ? 'sync' : 'async'}
                    fetchPriority={isLcp ? 'high' : 'low'}
                    sizes="(min-width: 1024px) 640px, 100vw"
                    className="aspect-square h-auto w-full rounded-3xl object-cover"
                  />
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>

      <div className="flex h-4 items-center justify-center gap-2 pt-6 lg:hidden">
        {images.map((_, index) => (
          <button
            type="button"
            key={index}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === selectedIndex ? 'true' : undefined}
            onClick={() => onDotButtonClick(index)}
            className={cn(
              'h-2 w-2 cursor-pointer rounded-full p-1 transition-all duration-300',
              index === selectedIndex ? 'h-3 w-3 bg-black' : 'bg-gray-300',
            )}
          />
        ))}
      </div>
    </div>
  );
});

ImageCarousel.displayName = 'ImageCarousel';

export default ImageCarousel;
